import { useGetRequestedLogisticsQuotesQuery } from "../../../store/apiSlices/logistics";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";
import { AirPlaneIcon, MapPinIcon, ShipIcon, SpinnerIcon } from "../../Icons";
import QuoteCard from "./QuoteCard";
import { RFQSkeletonList } from "./RFQs";

export default function OngoingOrders({
  onSelect,
}: {
  onSelect: (item: any) => void;
}) {
  const { data, isLoading, isError, refetch, isFetching } =
    useGetRequestedLogisticsQuotesQuery();

  if (isLoading) return <RFQSkeletonList />;

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <p className="text-sm text-gray-500">Failed to load quotes.</p>

        <button
          onClick={refetch}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700
               disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching && (
            <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-gray-600" />
          )}
          Try again
        </button>
      </div>
    );

  const quotes = data?.filter((el) => el.status === "confirmed") || [];

  if (!quotes.length)
    return <p className="text-center text-sm text-gray-400">No quotes found</p>;

  return (
    <div className="space-y-3">
      {quotes.map((quote) => (
        <QuoteCard
          key={quote.id || quote.quotes_id}
          quote={quote}
          onSelect={onSelect}
        >
          <QuoteCard.Header>
            <QuoteCard.FromTo />
            <QuoteCard.Status />
          </QuoteCard.Header>
          <QuoteCard.Body>
            <QuoteCard.FreightInfo />
            <QuoteCard.PickupDate />
          </QuoteCard.Body>
          <QuoteCard.Footer>
            <QuoteCard.CreatedAt />
          </QuoteCard.Footer>
        </QuoteCard>
      ))}
    </div>
  );
}

/* Expanded view for a single Draft item */
OngoingOrders.Expanded = function OngoingOrdersExpanded({
  selectedQuoteId,
  onClose,
}: {
  selectedQuoteId: LogisticsRequestQuote["id"];
  onClose: () => void;
}) {
  const { data } = useGetRequestedLogisticsQuotesQuery();

  const selectedQuote = data?.find((q) => q.id === selectedQuoteId);

  if (!selectedQuote) return null;

  const {
    from_json,
    to_json,
    freight_type,
    // loading_date,
    pickup_date,
    goods_category,
    weight,
    status,
    incoterm,
    actual_quotes,
  } = selectedQuote;

  const FreightIcon =
    freight_type?.toLowerCase() === "air" ? AirPlaneIcon : ShipIcon;

  const { label, className: statusColor } = getRFQStatusStyles(status);

  const confirmedQuote = actual_quotes?.find(
    (q) => q.status?.toLowerCase() === "confirmed",
  );

  return (
    <div className="h-full overflow-y-auto p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
      {/* Back Button */}
      <button
        onClick={onClose}
        className="mb-4 text-sm text-blue-600 hover:underline"
      >
        ← Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapPinIcon className="size-4 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {from_json?.name} → {to_json?.name}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FreightIcon className="size-4 text-blue-500" />
            <span>{freight_type?.toUpperCase()} Freight</span>
            <span className="text-gray-400">|</span>
            <span>{weight || "—"} kg</span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span>
              <span className="font-medium text-gray-600">Incoterm:</span>{" "}
              <span className="italic">
                {incoterm ? incoterm.toUpperCase() : "—"}
              </span>
            </span>

            <span className="text-gray-300">|</span>

            <span>
              <span className="font-medium text-gray-600">Goods:</span>{" "}
              <span className="text-gray-600">
                {goods_category || "Not specified"}
              </span>
            </span>
          </div>

          <p className="text-xs text-gray-400 mt-1">
            {/* Loading: {loading_date || "—"} |  */}
            Pickup: {pickup_date || "—"}
          </p>
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${statusColor}`}
        >
          {label || "Pending"}
        </span>
      </div>

      {confirmedQuote && (
        <div className="border-t border-gray-100 pt-6 mt-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Confirmed Vendor
          </h3>

          <div className="rounded-2xl border border-green-200 bg-green-50/60 p-5 shadow-sm">
            {/* Top Section */}
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-3">
                {/* Success Icon */}
                <div className="bg-green-100 text-green-600 rounded-full p-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                {/* Vendor Info */}
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {confirmedQuote.vendor}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expires: {confirmedQuote.expiry || "—"}
                  </p>
                </div>
              </div>

              {/* Price */}
              <div className="text-right">
                <p className="text-xl font-bold text-green-600">
                  ${confirmedQuote.price.toLocaleString()}
                </p>
                <p className="text-[11px] text-gray-400">Final Agreed Price</p>
              </div>
            </div>

            {/* Divider */}
            <div className="h-[1px] bg-green-200 my-4" />

            {/* Progress Message */}
            <p className="text-xs text-gray-600 leading-relaxed">
              Your shipment has been successfully confirmed with this vendor.
              We’ll keep you updated shortly as the logistics process
              progresses.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
